import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { CreditCard, Wallet, Plus } from 'lucide-react';
import { useCustomerCredits, CustomerCredit } from '@/hooks/useCustomerCredits';
import CreditActionsDropdown from './credits/CreditActionsDropdown';
import ApplyCreditsToInvoiceModal from './credits/ApplyCreditsToInvoiceModal';
import RefundCreditModal from './credits/RefundCreditModal';
import CancelCreditModal from './credits/CancelCreditModal';
import CreateManualCreditModal from './credits/CreateManualCreditModal';
import { ALL_CREDIT_REASON_TYPES } from './credits/CreateManualCreditModal';
import CreditNotePreviewModal from '@/components/credits/CreditNotePreviewModal';
import { format } from 'date-fns';
import { formatCurrency } from '@/lib/utils';

interface CustomerCreditsProps {
  customerId: string;
}

const CustomerCredits: React.FC<CustomerCreditsProps> = ({ customerId }) => {
  const navigate = useNavigate();
  const {
    credits,
    isLoading,
    totalAvailableCredit,
    applyCredit,
    isApplying,
    refundCredit,
    isRefunding,
    cancelCredit,
    isCancelling,
    generateCreditNote,
    isGeneratingCreditNote,
    createManualCredit,
    isCreating,
  } = useCustomerCredits(customerId);

  const [selectedCredit, setSelectedCredit] = useState<CustomerCredit | null>(null);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [previewCreditId, setPreviewCreditId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const getStatusBadge = (status: CustomerCredit['status']) => {
    switch (status) {
      case 'issued':
        return <Badge variant="default" className="bg-green-600">Issued</Badge>;
      case 'applied':
        return <Badge variant="secondary">Applied</Badge>;
      case 'refunded':
        return <Badge variant="outline">Refunded</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Cancelled</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getReasonLabel = (reasonType: string | null | undefined) => {
    if (!reasonType) return null;
    const found = ALL_CREDIT_REASON_TYPES.find(t => t.value === reasonType);
    return found?.label || reasonType;
  };

  const getSourceLabel = (sourceType: string, credit: CustomerCredit) => {
    switch (sourceType) {
      case 'overpayment':
        return credit.source_invoice?.invoice_number
          ? `Overpayment from ${credit.source_invoice.invoice_number}`
          : 'Overpayment';
      case 'manual':
        return 'Manual Credit';
      case 'refund':
        return 'Refund';
      default:
        return sourceType;
    }
  };

  const handleApply = (credit: CustomerCredit) => {
    setSelectedCredit(credit);
    setShowApplyModal(true);
  };

  const handleRefund = (credit: CustomerCredit) => {
    setSelectedCredit(credit);
    setShowRefundModal(true);
  };

  const handleCancel = (credit: CustomerCredit) => {
    setSelectedCredit(credit);
    setShowCancelModal(true);
  };

  const handleViewCreditNote = (credit: CustomerCredit) => {
    setPreviewCreditId(credit.id);
  };

  const handleViewDetails = (credit: CustomerCredit) => {
    navigate(`/credits/${credit.id}`);
  };

  const handleGenerateCreditNote = (credit: CustomerCredit) => {
    generateCreditNote({ creditId: credit.id });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Card */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Wallet className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Available Credit</p>
                <p className="text-2xl font-bold text-primary">{formatCurrency(totalAvailableCredit)}</p>
              </div>
            </div>
            <Button size="sm" onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4 mr-1" />
              Create Credit
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Credits List */}
      {credits && credits.length > 0 ? (
        <div className="space-y-3">
          {credits.map((credit) => (
            <Card
              key={credit.id}
              className="cursor-pointer hover:bg-accent/50 transition-colors"
              onClick={() => handleViewDetails(credit)}
            >
              <CardContent className="pt-4 pb-4">
                <div className="flex items-start justify-between gap-2 min-w-0">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <CreditCard className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <p className="text-sm font-medium truncate">
                        {getSourceLabel(credit.source_type, credit)}
                      </p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-base">
                          {formatCurrency(credit.remaining_amount)}
                        </span>
                        {credit.remaining_amount < credit.credit_amount && (
                          <span className="text-xs text-muted-foreground">
                            / {formatCurrency(credit.credit_amount)}
                          </span>
                        )}
                        {getStatusBadge(credit.status)}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
                        {(credit as any).reason_type && (
                          <Badge variant="secondary" className="text-[11px] px-1.5 py-0">
                            {getReasonLabel((credit as any).reason_type)}
                          </Badge>
                        )}
                        {credit.credit_note_number && (
                          <Badge variant="outline" className="font-mono text-[11px] px-1.5 py-0">
                            {credit.credit_note_number}
                          </Badge>
                        )}
                        <span>{format(new Date(credit.created_at), 'dd MMMM yyyy')}</span>
                      </div>
                      {credit.description && (
                        <p className="text-xs text-muted-foreground truncate">
                          {credit.description}
                        </p>
                      )}
                      {credit.cancelled_reason && (
                        <p className="text-xs text-destructive truncate">
                          Cancelled: {credit.cancelled_reason}
                        </p>
                      )}
                    </div>
                  </div>
                  <div onClick={(e) => e.stopPropagation()} className="shrink-0">
                    <CreditActionsDropdown
                      credit={credit}
                      onApply={() => handleApply(credit)}
                      onRefund={() => handleRefund(credit)}
                      onCancel={() => handleCancel(credit)}
                      onViewCreditNote={() => handleViewCreditNote(credit)}
                      onViewDetails={() => handleViewDetails(credit)}
                      onGenerateCreditNote={() => handleGenerateCreditNote(credit)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-muted-foreground">
              <CreditCard className="h-12 w-12 mx-auto mb-3 opacity-20" />
              <p>No credits found for this customer</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modals */}
      <ApplyCreditsToInvoiceModal
        isOpen={showApplyModal}
        onClose={() => {
          setShowApplyModal(false);
          setSelectedCredit(null);
        }}
        credit={selectedCredit}
        customerId={customerId}
        onApply={applyCredit}
        isLoading={isApplying}
      />

      <RefundCreditModal
        isOpen={showRefundModal}
        onClose={() => {
          setShowRefundModal(false);
          setSelectedCredit(null);
        }}
        credit={selectedCredit}
        onRefund={refundCredit}
        isLoading={isRefunding}
      />

      <CancelCreditModal
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          setSelectedCredit(null);
        }}
        credit={selectedCredit}
        onCancel={cancelCredit}
        isLoading={isCancelling}
      />

      {/* Credit Note Preview Modal */}
      <CreditNotePreviewModal
        creditId={previewCreditId || ''}
        isOpen={!!previewCreditId}
        onOpenChange={(open) => !open && setPreviewCreditId(null)}
      />
      {/* Create Manual Credit Modal */}
      <CreateManualCreditModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={(data) => {
          createManualCredit(data, {
            onSuccess: () => setShowCreateModal(false),
          });
        }}
        isLoading={isCreating}
      />
    </div>
  );
};

export default CustomerCredits;